import React, { useMemo } from 'react';
import type { PurchaseApprovalStep, PurchaseVarianceRequest } from '../../types/purchasing';
import {
  buildTrailItems,
  type ApprovalTrailFollowOn,
  type ApprovalTrailOrigin,
} from './ApprovalMap';
import PurchaseLinkedText from './PurchaseLinkedText';
import { initialsFromAgentLabel, isAgentRequesterLabel } from '../../utils/purchaseRequesterDisplay';
import './PurchaseApprovalBreadcrumb.css';

export function initialsFromDisplayName(name: string | undefined): string {
  const trimmed = String(name || '').trim();
  if (!trimmed) return '—';
  if (isAgentRequesterLabel(trimmed)) {
    return initialsFromAgentLabel(trimmed) || 'AG';
  }
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    const p = parts[0];
    if (p.length <= 2) return p.toUpperCase();
    return `${p[0]}${p[p.length - 1]}`.toUpperCase();
  }
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function initialsFromRoleLabel(label: string | undefined): string {
  const words = String(label || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
}

function crumbInitials(userName: string | undefined, roleLabel: string): string {
  if (userName?.trim()) return initialsFromDisplayName(userName);
  return initialsFromRoleLabel(roleLabel);
}

type TrailItem = ReturnType<typeof buildTrailItems>[number];

function withVarianceStep(items: TrailItem[], variance: PurchaseVarianceRequest | undefined): TrailItem[] {
  if (!variance?.reviewerName?.trim()) return items;
  const varianceItem: TrailItem = {
    key: 'variance',
    roleLabel: 'Actual amount',
    userName: variance.reviewerName,
    state: 'current',
    status: 'Waiting on variance approval',
  };
  const followIdx = items.findIndex((i) => i.key === 'follow-on');
  if (followIdx < 0) return [...items, varianceItem];
  const next = [...items];
  next.splice(followIdx, 0, varianceItem);
  const follow = next[followIdx + 1];
  if (follow?.key === 'follow-on') {
    next[followIdx + 1] = { ...follow, state: 'todo', status: 'Next' };
  }
  return next;
}

const PurchaseApprovalBreadcrumb: React.FC<{
  steps: PurchaseApprovalStep[];
  origin?: ApprovalTrailOrigin;
  followOn?: ApprovalTrailFollowOn;
  variance?: PurchaseVarianceRequest;
}> = ({ steps, origin, followOn, variance }) => {
  const items = useMemo(() => {
    const base = buildTrailItems(steps, origin, followOn);
    return withVarianceStep(base, variance);
  }, [steps, origin, followOn, variance]);

  if (items.length === 0) {
    return <span className="po-trail-empty">—</span>;
  }

  return (
    <span
      className="po-trail-crumb"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {items.map((item, idx) => (
        <React.Fragment key={item.key}>
          {idx > 0 && (
            <span className="po-trail-sep" aria-hidden>
              ›
            </span>
          )}
          <span
            className={`po-trail-node po-trail-node--${item.state}`}
            title={`${item.roleLabel}${item.userName ? ` · ${item.userName}` : ''}`}
          >
            {crumbInitials(item.userName, item.roleLabel)}
          </span>
        </React.Fragment>
      ))}
      <span className="po-trail-tip" role="tooltip">
        <span className="po-trail-tip-title">Approval path</span>
        <ol className="po-trail-tip-list">
          {items.map((item) => (
            <li key={`tip-${item.key}`} className={`po-trail-tip-row po-trail-tip-row--${item.state}`}>
              <span className="po-trail-tip-role">{item.roleLabel}</span>
              {item.userName && <span className="po-trail-tip-name">{item.userName}</span>}
              <span className="po-trail-tip-status">{item.status}</span>
              {item.note?.trim() && (
                <span className="po-trail-tip-note">
                  <PurchaseLinkedText text={item.note.trim()} />
                </span>
              )}
            </li>
          ))}
        </ol>
      </span>
    </span>
  );
};

export default PurchaseApprovalBreadcrumb;
